import PropTypes from 'prop-types';

import { AlertTriangle, Info } from 'lucide-react';

/**
 * A quiet, token-styled note row (CT-5191 / CT-5193 / CT-5194).
 *
 * The tickets call for `StatusMessage variant="info"` here, but that component
 * takes no `variant` and always renders a centred block with `py-16` — correct
 * for an empty page, far too loud for a one-line footnote under a picker. This
 * is the note row the tickets describe for the empty states, reused for the
 * "more available" footnotes so every quiet note in the step looks the same.
 *
 * **Two tones.** `info` states a fact — no groupers means a single value, and
 * that is a legitimate widget. `warning` says the widget as configured will be
 * refused, which is a different thing and has to read as one; it borrows the
 * amber set `ShareConfirmationStep` already uses, so the two are one system.
 *
 * Colours come from surface/edge/content tokens, and from that amber set.
 */
function InlineNote({ children, icon, tone = 'info' }) {
  const warning = tone === 'warning';
  const Icon = icon ?? (warning ? AlertTriangle : Info);

  return (
    <div
      className={[
        'flex min-w-0 items-start gap-2 rounded-md border px-3 py-2',
        warning
          ? 'border-amber-200 bg-amber-50 dark:border-amber-400/80 dark:bg-amber-950/35'
          : 'border-edge bg-surface-muted',
      ].join(' ')}
    >
      <Icon
        size={14}
        className={[
          'mt-px shrink-0',
          warning ? 'text-amber-800 dark:text-amber-400' : 'text-content-muted',
        ].join(' ')}
        aria-hidden
      />
      <p
        className={[
          'm-0 min-w-0 text-xs',
          warning ? 'text-amber-900 dark:text-amber-200' : 'text-content-muted',
        ].join(' ')}
      >
        {children}
      </p>
    </div>
  );
}

InlineNote.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.elementType,
  /** `warning` for a note that says the widget will be refused as configured. */
  tone: PropTypes.oneOf(['info', 'warning']),
};

export default InlineNote;
